import React, { FC, MouseEventHandler, useState } from 'react';

import { useIntl } from 'react-intl';

import { ActionLink } from 'components/action-button';
import { CanadaCheckoutLimitMessage } from 'components/canada-order-requirements/canada-checkout-limit-message';
import { CheckoutErrorsModal } from 'components/checkout-errors-modal';
import FullPageScreen from 'components/full-page-screen';
import PaymentMethod from 'components/payment-method';
import WarningMessage from 'components/warning-message';
import { useGetPaymentErrors } from 'hooks/use-get-payment-errors';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useOrderContext } from 'state/order';
import { useCanadaDeliveryLimitState } from 'state/order/hooks/use-canada-order-policy';
import { usePaymentContext } from 'state/payment';
import { useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import noop from 'utils/noop';
import { checkServiceModeUnavailable } from 'utils/restaurant';
import { relativeRoutes, routes } from 'utils/routing';
import { getLocalizedServiceMode } from 'utils/service-mode';

import { PlaceOrderButton } from '../../place-order-button';
import AddNewCard from '../add-new-card';
import DeliveryCancellationPolicy from '../delivery-cancellation-policy';
import PrepaidMethodReload from '../prepaid-method-reload/prepaid-method-reload.th';
import {
  ActionContainer,
  AddNewCardWrapper,
  ButtonContainer,
  ErrorContainer,
  ErrorMessage,
  PaymentSection,
  PaymentSelection,
  SmallText,
  StyledIconTimCard,
  StyledSectionHeading,
  StyledTextButton,
} from '../styled.th';
import { OrderPaymentProps } from '../types';

import Totals from './Totals';
import { TermsContainer, TermsLink, WarningContainer } from './styled.th';
import { useOrderPayment } from './use-order-payment.th';
import { getNativePaymentDisclaimer } from './utils';

export const Payment: FC<OrderPaymentProps> = ({
  serverOrder,
  onPriceOrder,
  pricedAndAvailable,
}) => {
  const { formatMessage } = useIntl();
  const checkoutDeliveryPriceMinimum = useFlag(LaunchDarklyFlag.OVERRIDE_CHECKOUT_DELIVERY_MINIMUM);
  const order = useOrderContext();
  const canadaDeliveryLimitState = useCanadaDeliveryLimitState({
    cartPriceLimitExceeded: Boolean(order.cartPriceLimitExceeded),
    cartPriceTooLow: order.calculateCartTotal() < checkoutDeliveryPriceMinimum,
  });
  const { store } = useStoreContext();
  const {
    checkoutPaymentMethodId: defaultPaymentMethod,
    canUseApplePay,
    canUseGooglePay,
  } = usePaymentContext();
  const { serviceMode, isCurbside, isDelivery } = useServiceModeContext();
  const { getErrorMessages } = useGetPaymentErrors();
  const [isCheckoutErrorsModalOpen, setIsCheckoutErrorsModalOpen] = useState(false);

  const {
    checkoutPaymentMethodId,
    checkoutPaymentMethodNeedRevault,
    confirmStoreOnCheckout,
    enableDirectPay,
    ErrorDialog,
    handleOrderCommit,
    isApplePayLoading,
    isCateringOrder,
    isGooglePayLoading,
    isLoading,
    isValidTip,
    LoadPrepaidCardDialog,
    needReloadAgain,
    openLoadPrepaidCardDialog,
    openServiceModeUnavailableErrorDialog,
    openStoreConfirmationModal,
    openTransferBalanceDialog,
    paymentOptions,
    placeOrderAriaLabel,
    prepaidIsApplePayLoading,
    prepaidMethodSelected,
    prepaidPaymentMethod,
    reloadAmount,
    reloadIsValid,
    ServiceModeUnavailableErrorDialog,
    setCheckoutPaymentMethodId,
    setCommitOrderAfterPricing,
    showNewReloadMethod,
    showReloadPaymentMethods,
    StoreConfirmationDialog,
  } = useOrderPayment({
    serverOrder,
    defaultPaymentMethod,
  });

  const enableTermsLinkOnCheckout = useFlag(LaunchDarklyFlag.ENABLE_TERMS_LINK_ON_CHECKOUT);

  const nativePaymentDisclaimer = getNativePaymentDisclaimer(
    canUseApplePay,
    canUseGooglePay,
    formatMessage
  );

  const onPlaceOrderClick: MouseEventHandler = () => {
    if (!isCateringOrder && checkServiceModeUnavailable(order.serviceMode, store)) {
      openServiceModeUnavailableErrorDialog({
        message: formatMessage(
          { id: 'currentServiceModeUnavailable' },
          {
            serviceMode: getLocalizedServiceMode(order.serviceMode, formatMessage),
          }
        ),
      });
      return;
    }

    if (hasErrors) {
      setIsCheckoutErrorsModalOpen(true);
      return;
    }

    if (isCurbside && onPriceOrder) {
      setCommitOrderAfterPricing(true);
      return onPriceOrder({
        customerName: `CS-${LocalStorage.getItem(StorageKeys.VEHICLE_DESCRIPTION)}`,
      });
    }

    if (isDelivery && onPriceOrder) {
      setCommitOrderAfterPricing(true);
      return onPriceOrder({ tipCents: order.tipAmount });
    }

    if (!isDelivery && confirmStoreOnCheckout) {
      return openStoreConfirmationModal();
    }

    return handleOrderCommit();
  };

  const paymentErrorMessages = getErrorMessages({
    checkoutPaymentMethodNeedRevaultError: checkoutPaymentMethodNeedRevault,
    invalidReloadError: !reloadIsValid,
    invalidTipAmountError: !isValidTip,
    noDirectPayAndNoPrepaidMethodError: !enableDirectPay && !prepaidPaymentMethod,
    pricedAndAvailableError: !pricedAndAvailable,
  });

  const hasErrors = !!paymentErrorMessages.length;

  const isProcessingPayment =
    isLoading || isApplePayLoading || prepaidIsApplePayLoading || isGooglePayLoading;

  return (
    <FullPageScreen
      heading={formatMessage({ id: 'orderPayment' })}
      closeHref={routes.cart}
      disableClose={isProcessingPayment}
    >
      {isCheckoutErrorsModalOpen && (
        <CheckoutErrorsModal
          checkoutErrors={paymentErrorMessages}
          closeModal={() => {
            setIsCheckoutErrorsModalOpen(false);
          }}
        />
      )}
      <PaymentSection data-testid="cart-payment">
        <div>
          <StyledSectionHeading>{formatMessage({ id: 'paymentMethod' })}</StyledSectionHeading>
          <PaymentSelection>
            {(enableDirectPay || prepaidPaymentMethod) && (
              <PaymentMethod
                aria-expanded="true"
                handleSelect={setCheckoutPaymentMethodId}
                hasBorder
                methods={paymentOptions}
                openAddCardOnClick={noop}
                selectedMethodId={checkoutPaymentMethodId}
                showCollapsibleMenu={enableDirectPay}
                checkoutPaymentMethodNeedRevault={checkoutPaymentMethodNeedRevault}
                enableDirectPay={enableDirectPay}
              />
            )}
            {!prepaidPaymentMethod && (
              <AddNewCardWrapper addMargin={enableDirectPay && !prepaidPaymentMethod}>
                <AddNewCard
                  text={formatMessage({ id: 'addTimCard' })}
                  icons={[StyledIconTimCard]}
                  onClick={openLoadPrepaidCardDialog}
                  data-testid="load-timcard"
                />
                <ButtonContainer>
                  <StyledTextButton onClick={openTransferBalanceDialog}>
                    {formatMessage({ id: 'transferBalanceFromAnotherTimCard' })}
                  </StyledTextButton>
                </ButtonContainer>
              </AddNewCardWrapper>
            )}

            {showNewReloadMethod && (
              <ActionContainer>
                <>
                  <SmallText>
                    {formatMessage({ id: 'balanceNoCoverCostReloadToContinue' })}
                  </SmallText>
                  <ButtonContainer>
                    <ActionLink to={relativeRoutes.addCard}>
                      {formatMessage({ id: 'loadTimCard' })}
                    </ActionLink>
                  </ButtonContainer>
                </>
                <ButtonContainer>
                  <StyledTextButton onClick={openTransferBalanceDialog}>
                    {formatMessage({ id: 'transferBalanceFromAnotherTimCard' })}
                  </StyledTextButton>
                </ButtonContainer>
              </ActionContainer>
            )}
          </PaymentSelection>

          {showReloadPaymentMethods && prepaidMethodSelected && (
            <PrepaidMethodReload reloadAmount={reloadAmount} />
          )}
        </div>

        {needReloadAgain && (
          <ErrorContainer>
            <ErrorMessage>{formatMessage({ id: 'insufficientBalanceReloadAgain' })}</ErrorMessage>
          </ErrorContainer>
        )}
        <Totals
          order={order}
          serverOrder={serverOrder}
          serviceMode={serviceMode}
          isDisabled={isProcessingPayment}
        />
        {!checkoutPaymentMethodId && nativePaymentDisclaimer && (
          <WarningContainer>
            <WarningMessage message={nativePaymentDisclaimer} />
          </WarningContainer>
        )}
        <CanadaCheckoutLimitMessage state={canadaDeliveryLimitState} />
        <PlaceOrderButton
          confirmation={!confirmStoreOnCheckout || isDelivery}
          disabled={isProcessingPayment || canadaDeliveryLimitState.kind !== 'clear'}
          isLoading={isProcessingPayment}
          onClick={onPlaceOrderClick}
          reloadAmount={prepaidMethodSelected ? reloadAmount : undefined}
          placeOrderAriaLabel={placeOrderAriaLabel}
        />
        {isDelivery && <DeliveryCancellationPolicy />}
        {enableTermsLinkOnCheckout && (
          <TermsContainer>
            <TermsLink to={formatMessage({ id: 'routes.mobileOrderingAndPaymentTerms' })}>
              {formatMessage({ id: 'mobileOrderingAndPaymentTerms' })}
            </TermsLink>
          </TermsContainer>
        )}
        <LoadPrepaidCardDialog loadAmount={reloadAmount} />
        <ErrorDialog />
        <StoreConfirmationDialog onConfirm={handleOrderCommit} />
        <ServiceModeUnavailableErrorDialog data-testid="servicemode-unavailable-error-modal" />
      </PaymentSection>
    </FullPageScreen>
  );
};
